import * as yup from 'yup';

export const VALIDATION_CONSTANTS = {
  PASSWORD_PARTS: {
    NUMBER: /[0-9]/g,
    LOWERCASE: /[a-z]/g,
    UPPERCASE: /[A-Z]/g,
    SPECIAL_CHAR: /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/,
  },
};

export const loginSchema = yup.object().shape({
  email: yup.string().email().trim().required().label('Email'),
  password: yup.string().trim().min(6).required().label('Password'),
});

export const accountSettingsSchema = yup.object().shape({
  email: yup.string().email().trim().required().label('Email'),
  password: yup.string().trim().min(6).required().label('Password'),
  phone_number: yup
    .string()
    .trim()
    .optional()
    .test(
      'phone-format',
      'Please enter a valid phone number',
      function (value) {
        if (!value) return true; // Allow empty since it's optional

        // Remove all non-digit characters except +
        const cleaned = value.replace(/[^\d+]/g, '');

        // Check length first
        let digitsOnly = cleaned;
        let isValidLength = false;

        // International format: starts with + and has 7-15 digits after country code
        if (cleaned.startsWith('+')) {
          digitsOnly = cleaned.substring(1); // Remove the + for pattern checking
          isValidLength = cleaned.length >= 8 && cleaned.length <= 16;
        } else {
          // Local format: 7-15 digits (covers most countries)
          isValidLength = cleaned.length >= 7 && cleaned.length <= 15;
        }

        if (!isValidLength) return false;

        // Check if all digits are the same (e.g., 888888888888)
        if (/^(\d)\1+$/.test(digitsOnly)) {
          return false;
        }

        // Check if all digits are zeros (e.g., 0000000000)
        if (/^0+$/.test(digitsOnly)) {
          return false;
        }

        // Check for sequential ascending digits (e.g., 1234567890)
        const isSequentialAscending = digitsOnly.split('').every((digit, index) => {
          if (index === 0) return true;
          const prevDigit = parseInt(digitsOnly[index - 1]);
          const currDigit = parseInt(digit);
          return currDigit === (prevDigit + 1) % 10; // Allow wrapping (9 -> 0)
        });

        // Check for sequential descending digits (e.g., 9876543210)
        const isSequentialDescending = digitsOnly.split('').every((digit, index) => {
          if (index === 0) return true;
          const prevDigit = parseInt(digitsOnly[index - 1]);
          const currDigit = parseInt(digit);
          return currDigit === (prevDigit - 1 + 10) % 10; // Allow wrapping (0 -> 9)
        });

        if (isSequentialAscending || isSequentialDescending) {
          return false;
        }

        // Check for alternating pattern (e.g., 1212121212)
        const isAlternating = digitsOnly.length >= 4 &&
          /^(\d{2})\1+$/.test(digitsOnly);

        if (isAlternating) {
          return false;
        }

        return true;
      }
    )
    .label('Phone Number'),
});

export const accountDeletionSchema = yup.object().shape({
  reason: yup.string().trim().optional(),
  changes: yup.string().trim().optional(),
  deletionSuggestions: yup.string().trim().optional(),
});

export const forgotPasswordSchema = yup.object().shape({
  email: yup.string().email().trim().required().label('Email'),
});

export const resetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .trim()
    .required()
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.LOWERCASE,
      'Password should contain a lowercase character',
    )
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.UPPERCASE,
      'Password should contain an uppercase character',
    )
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.SPECIAL_CHAR,
      'Password should contain a special character',
    )
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.NUMBER,
      'Password should contain a number',
    )
    .min(6)
    .label('Password'),
  confirmPassword: yup
    .string()
    .trim()
    .required()
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.LOWERCASE,
      'Password should contain a lowercase character',
    )
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.UPPERCASE,
      'Password should contain an uppercase character',
    )
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.SPECIAL_CHAR,
      'Password should contain a special character',
    )
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.NUMBER,
      'Password should contain a number',
    )
    .min(6)
    .label('Confirm Password'),
});

export const signUpSchema = yup.object().shape({
  first_name: yup.string().trim().required().label('First Name'),
  last_name: yup.string().trim().required().label('Last Name'),
  email: yup.string().email().trim().required().label('Email'),
  password: yup
    .string()
    .trim()
    .required()
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.LOWERCASE,
      'Password should contain a lowercase character',
    )
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.UPPERCASE,
      'Password should contain an uppercase character',
    )
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.SPECIAL_CHAR,
      'Password should contain a special character',
    )
    .matches(
      VALIDATION_CONSTANTS.PASSWORD_PARTS.NUMBER,
      'Password should contain a number',
    )
    .min(6)
    .label('Password'),
  referral_source: yup
    .string()
    .trim()
    .required('Please select how you heard about us')
    .label('How did you hear about us?'),
  referral_source_detail: yup
    .string()
    .trim()
    .optional()
    .label('Please specify'),
});

export const addAdvertSchema = yup.object().shape({
  agency_id: yup.number().optional().label('Brand Id').nullable(true),
  description: yup.string().trim().optional().label('Description'),
  content: yup.string().trim().optional().label('Content'),
  title: yup.string().trim().optional().label('Advert title'),
  advert_type: yup
    .string()
    .trim()
    .matches(/inbox|default|pin_to_feed/)
    .required()
    .label('Advert Type'),
  user_type: yup
    .string()
    .trim()
    .optional()
    .matches(/all|founder|expert|investor/)
    .label('User Type'),
  external_link: yup.string().trim().url().optional().label('External Link'),
  start_date: yup
    .date()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .required('Start date is required'),
  end_date: yup
    .date()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .required('End date is required'),
  attachment: yup.mixed().optional().label('Advert image'),
});

export const addExpertSchema = yup.object().shape({
  first_name: yup.string().trim().required().label('First Name'),
  last_name: yup.string().trim().required().label('Last Name'),
  email: yup.string().trim().required().label('Email'),
  password: yup.string().trim().min(8).required().label('Password'),
  phone_number: yup.string().trim().optional().label('Phone Number'),
  headline: yup.string().trim().required().label('Position'),
  bio: yup.string().trim().required().label('Bio'),
  company_name: yup.string().trim().required().label('Company Name'),
});
